import redirectInterceptor from "@/interceptors/redirect_interceptor";
import qs from "qs";

import voyageRouteDataFromJson from "../../domain/weather_routing/voyage_route/mapper";
import HttpError from "../../services/http_error";
import WeatherRoutingVoyageService from "../../services/voyage/service";
import createVoyageDataToJson from "../../domain/weather_routing/create_voyage/mapper_to_json";
import voyageDataFromJson from "../../domain/weather_routing/voyage/db/mapper";
import voyageComputationStateFromJson from "../../domain/weather_routing/calculation_state/db/mapper";
import WeatherRoutingVoyageSolutionsService from "../../services/voyage_solutions/service";
import computationDataFromJson from "../../domain/weather_routing/computation/mapper";
import updateVesselPositionDataToJson from "../../domain/weather_routing/update_vessel_position/mapper_to_json";
import voyageMeshDataFromJson from "../../domain/weather_routing/voyage_mesh/mapper";
import UpdateVesselPositionData from "../../domain/weather_routing/update_vessel_position/data";
import TimedIntervalManager from "../../helper_functions/timed_interval_interface";

const calculationIntervalManager = new TimedIntervalManager();
const localWeatherRoutingServerUrl = process.env.VUE_APP_LOCAL_WR_API_URL;
const companyId = process.env.VUE_APP_COMPANY_ID;

const voyageService = new WeatherRoutingVoyageService(
  localWeatherRoutingServerUrl,
  companyId,
  createVoyageDataToJson,
  voyageDataFromJson,
  voyageComputationStateFromJson,
);

const voyageSolutionService = new WeatherRoutingVoyageSolutionsService(
  localWeatherRoutingServerUrl,
  voyageRouteDataFromJson,
  computationDataFromJson,
  updateVesselPositionDataToJson,
  voyageMeshDataFromJson
);

const getDefaultState = () => {
  const timeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };

  return {
    voyageSolutions: null,
    voyageComputations: [],
    selectedComputation: null,
    fetchSolutionsLoading: true,
    fetchSolutionsError: null,

    recalculationLoading: false,
    recalculationError: null,
    recalculationState: "",

    latitude: 0,
    longitude: 0,
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
    time: new Date().toLocaleTimeString("en-GB", timeFormatOptions),
    voyageSolutionsMesh: null,

    computationInProgress: null, // The current computation objec that represents the calculation state
  };
};

const state = getDefaultState();

const getters = {
  fetchRequestFailed: (state) => (state.fetchSolutionsError !== null),
  noVoyageSolutions: (state) => (
    state.voyageSolutions === null || state.voyageSolutions.routes.length === 0
  ),
  recalculationFailed: (state) => (state.recalculationError !== null),
  getFoaGeoJson: (state) => {
    const foaFeaturesData = state.voyageSolutions !== null ? state.voyageSolutions.foaFeaturesDataList : [];

    const pointFeatures = foaFeaturesData.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {

          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "foa",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: foaFeaturesData.map((feature) => feature.coordinates),
            },
            properties: {
              id: "foa",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },
  getMinFuelGeoJson: (state) => {
    const minFuelFeaturesData = state.voyageSolutions !== null ? state.voyageSolutions.minFuelFeaturesDataList : [];
    const pointFeatures = minFuelFeaturesData.map((feature) => {
      return {
        "type": "Feature",

        "geometry": {
          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "minFuel",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: minFuelFeaturesData.map((feature) => feature.coordinates),
            },
            properties: {
              id: "minFuel",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },
  getMinTimeGeoJson: (state) => {
    const minTimeFeaturesData = state.voyageSolutions !== null ? state.voyageSolutions.minTimeFeaturesDataList : [];
    const pointFeatures = minTimeFeaturesData.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {

          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "minTime",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: minTimeFeaturesData.map((feature) => feature.coordinates),
            },
            properties: {
              id: "minTime",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },
  getBestTceGeoJson: (state) => {
    const bestTceFeaturesData = state.voyageSolutions !== null ? state.voyageSolutions.bestTceFeaturesDataList : [];
    const pointFeatures = bestTceFeaturesData.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {

          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "bestTce",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: bestTceFeaturesData.map((feature) => feature.coordinates),
            },
            properties: {
              id: "bestTce",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },
  getBestDtaGeoJson: (state) => {
    const bestDtaFeaturesData = state.voyageSolutions !== null ? state.voyageSolutions.bestDtaFeaturesDataList : [];

    const pointFeatures = bestDtaFeaturesData.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {

          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "bestDta",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: bestDtaFeaturesData.map((feature) => feature.coordinates),
            },
            properties: {
              id: "bestDta",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },

  getHighFeaturesGeoJson: (state) => {
    const highFeaturesDataList = state.voyageSolutionsMesh !== null ? state.voyageSolutionsMesh.highFeaturesDataList : [];

    const pointFeatures = highFeaturesDataList.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {
          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateMeshFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "high",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: highFeaturesDataList.map((feature) => feature.coordinates),
            },
            properties: {
              id: "high",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },

  getLowFeaturesGeoJson: (state) => {
    const lowFeaturesDataList = state.voyageSolutionsMesh !== null ? state.voyageSolutionsMesh.lowFeaturesDataList : [];
    const pointFeatures = lowFeaturesDataList.map((feature) => {
      return {
        "type": "Feature",
        "geometry": {
          "type": "Point",
          "coordinates": feature.coordinates
        },
        "properties": {
          "description": _generateMeshFeatureDataHTML(feature),
        }
      };
    });

    return {
      type: "geojson",
      data: {
        id: "low",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: lowFeaturesDataList.map((feature) => feature.coordinates),
            },
            properties: {
              id: "low",
            },

          },
          ...pointFeatures
        ],
      },
    };
  },

  getSolutionsSpeedChartData: (state) => {
    return state.voyageSolutions !== null ? _generateTimeSeriesData(
      [
        { data: state.voyageSolutions.foaSpeedSeriesData, name: "FOA" },
        { data: state.voyageSolutions.minFuelSpeedSeriesData, name: "Min Fuel" },
        { data: state.voyageSolutions.minTimeSpeedSeriesData, name: "Min Time" },
        { data: state.voyageSolutions.bestTceSpeedSeriesData, name: "Best Tce" },
        { data: state.voyageSolutions.bestDtaSpeedSeriesData, name: "Best Dta" },
      ],
      "speed",
      "knots",
      false,
      true,
      2,
      "Voyage Speeds"
    ) : {};
  },

  getSolutionsDailyConsumptionChartData: (state) => {
    return state.voyageSolutions !== null ? _generateTimeSeriesData(
      [
        { data: state.voyageSolutions.foaDailyConsumptionSeriesData, name: "FOA" },
        { data: state.voyageSolutions.minFuelDailyConsumptionSeriesData, name: "Min Fuel" },
        { data: state.voyageSolutions.minTimeDailyConsumptionSeriesData, name: "Min Time" },
        { data: state.voyageSolutions.bestTceDailyConsumptionSeriesData, name: "Best Tce" },
        { data: state.voyageSolutions.bestDtaDailyConsumptionSeriesData, name: "Best Dta" },
      ],
      "consumption",
      "metric tons",
      true,
      true,
      5,
      "Daily Fuel Consumption"

    ) : [];

  },
  voyageHasDtaSolution: (state) => {
    return state.voyageSolutions !== null && state.voyageSolutions.bestDtaFeatures !== null;
  },

  loadingComputation: (state) => {
    const computation = state.recalculationState.computation;

    return state.voyageComputations.find((computationObject) => computationObject.computation === computation);
  },
  showRecalculationLoading: (state, getters) => {
    return state.recalculationLoading && getters.loadingComputation.computation === state.selectedComputation.commit;
  }
};

const actions = {
  async getVoyageSolutions({ commit, rootState }, payload) {
    const voyageId = payload.voyageId;
    const computationId = payload.computationId;
    try {
      const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
      const voyageSolutions =
            await voyageSolutionService.getVoyageSolutions(localStorage.getItem("quad_token"), theyrToken, voyageId, computationId);
      commit("SET_FETCH_SUCCESS_STATE", voyageSolutions);

      const voyageSolutionsMesh =
            await voyageSolutionService.getVoyageSolutionsMesh(localStorage.getItem("quad_token"), theyrToken, voyageId, computationId);
      commit("SET_VOYAGE_SOLUTIONS_MESH", voyageSolutionsMesh);
    } catch (e) {
      commit("SET_FETCH_FAILED_STATE");
    }
    commit("SET_LOADING_STATE", false);
  },

  async getVoyageComputations({ commit, rootState }, voyageId) {
    try {
      const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
      const voyageComputations = await voyageSolutionService.getVoyageComputations(localStorage.getItem("quad_token"), theyrToken, voyageId);
      commit("SET_VOYAGE_COMPUTATIONS", voyageComputations);
    } catch (e) {
      commit("SET_VOYAGE_COMPUTATIONS", []);
    }
  },

  async exportVoyageData({ rootState }, exportVoyagePayload) {
    const fileFormat = exportVoyagePayload.fileFormat;
    const optimizationTypes = exportVoyagePayload.optimizationTypes;
    const params = {
      format: fileFormat,
      speedUnit: "kn",
      type: [],
      directionUnit: "degree",
      excludeSchedule: "false"
    };

    optimizationTypes.map((type) => params.type.push(type));
    const response = await redirectInterceptor.get(
      `${localWeatherRoutingServerUrl}/export_voyage_solutions/${state.voyageSolutions.voyageId}`,
      {
        headers: {
          "access-token": `${localStorage.getItem("quad_token")}`,
          "theyr-token": `${rootState.weatherRouting.weatherRoutingAccessToken}`
        },
        params: params,
        responseType: "blob",
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: "repeat", indices: false });
        },
      }
    );
    return response.data;
  },

  async pushVoyageRecalculation({ commit, rootState, dispatch }, payload) {
    // Clear any existing intervals first
    commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
    commit("SET_CALCULATION_LOADING");
    const voyageId = payload.voyageId;
    const updateVesselPosition = payload.updateVesselPosition;
    // const callbackFunction = payload.callback

    try {
      if (updateVesselPosition) {
        await _updateVesselCoordinates(rootState, voyageId);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        commit("SET_CALCULATION_FAILED", error.message);
      }
      else {
        commit("SET_CALCULATION_FAILED", "Failed to update vessel coordinates");
      }
      return;
    }

    const computation = await _postVoyageCalculation(commit, rootState, voyageId);
    commit("SET_COMPUTATION_IN_PROGRESS", computation);
    await dispatch("getVoyageComputations", voyageId);
    await dispatch("checkSelectedComputationState", voyageId);

  },

  async checkSelectedComputationState({ commit, rootState, dispatch }, voyageId) {
    commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
    commit("SET_FETCH_LOADING_STATE");
    const computationState = await _getCalculationState(
      localStorage.getItem("quad_token"),
      rootState,
      state.selectedComputation.id
    );
    if (computationState.stateValue === 0) {
      await dispatch("getVoyageSolutions", {
        voyageId: voyageId,
        computationId: state.selectedComputation.computation
      });
      commit("SET_LOADING_STATE", false);
    }
    else if (computationState.stateValue > 0) {
      commit("SET_COMPUTATION_IN_PROGRESS", state.selectedComputation);
      commit("SET_CALCULATION_LOADING");
      calculationIntervalManager.createInterval(
        () => _handleRecalculationState(
          commit,
          rootState,
          () => dispatch("_onRecalculationSuccess", voyageId),
          voyageId,
          state.selectedComputation
        ),
        1500,
        600000,
        () => {
          commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
          commit("SET_CALCULATION_FAILED", "Computation time exceeded");
        }
      );
    }
    else {
      commit("SET_FETCH_FAILED_STATE");
    }
  },

  async _onRecalculationSuccess({ dispatch }, voyageId) {
    await dispatch("getVoyageComputations", voyageId);
    const computation = state.voyageComputations.find((computation) => computation.id === state.computationInProgress.id);

    await dispatch("getVoyageSolutions", {
      voyageId: voyageId,
      computationId: computation.computation
    });
  }
};

const mutations = {
  SET_VOYAGE_SOLUTIONS: (state, data) => (state.voyageSolutions = data),
  SET_VOYAGE_SOLUTIONS_MESH: (state, data) => (state.voyageSolutionsMesh = data),
  SET_VOYAGE_COMPUTATIONS(state, data) {
    state.voyageComputations = data;
    if (data && data.length > 0) {
      state.selectedComputation = data[0];
    }
  },
  SET_SELECTED_COMPUTATION: (state, data) => (state.selectedComputation = data),
  SET_FETCH_LOADING_STATE(state) {
    state.fetchSolutionsLoading = true;
    state.fetchSolutionsError = null;
  },
  SET_FETCH_FAILED_STATE(state) {
    state.fetchSolutionsLoading = false;
    state.fetchSolutionsError = "Could not fetch solutions for given voyage";
  },
  SET_FETCH_SUCCESS_STATE(state, data) {
    state.fetchSolutionsLoading = false;
    state.fetchSolutionsError = null;
    state.voyageSolutions = data;
  },
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_RECALCULATION_STATE: (state, data) => (state.recalculationState = data),
  SET_LOADING_STATE: (state, data) => (state.recalculationLoading = data),
  SET_CALCULATION_LOADING(state) {
    state.recalculationError = null;
    state.recalculationLoading = true;
    state.recalculationState = "";
  },
  SET_CALCULATION_FAILED(state, data) {
    state.recalculationLoading = false;
    state.recalculationError = data;
    state.recalculationState = "";
  },
  SET_CALCULATION_SUCCESS(state) {
    state.recalculationLoading = false;
    state.recalculationError = null;
    state.recalculationState = "";
  },
  SET_LATITUDE: (state, data) => (state.latitude = data),
  SET_LONGITUDE: (state, data) => (state.longitude = data),
  SET_DATE: (state, data) => (state.date = data),
  SET_TIME: (state, data) => (state.time = data),
  SET_COMPUTATION_IN_PROGRESS: (state, data) => (state.computationInProgress = data),
  RESET_CALCULATION_INTERVAL_AND_TIMEOUT: () => calculationIntervalManager.clearAll()
};

async function _handleRecalculationState(
  commit, rootState, callbackFunction, voyageId, computation
) {
  try {
    const recalculationState = await _getCalculationState(
      localStorage.getItem("quad_token"),
      rootState,
      computation.id
    );
    commit("SET_RECALCULATION_STATE", recalculationState);
    if (recalculationState.stateValue === 0) {
      commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
      await _getCalculationState(
        localStorage.getItem("quad_token"),
        rootState,
        computation.id
      );
      callbackFunction(voyageId);
      commit("SET_CALCULATION_SUCCESS");
    }
    else if (recalculationState.stateValue < 0) {
      commit("SET_CALCULATION_FAILED", recalculationState.stateTitle);
      commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
    }
  } catch (e) {
    if (e instanceof HttpError) {
      commit("SET_CALCULATION_FAILED", `${e.message}`);
      commit("RESET_CALCULATION_INTERVAL_AND_TIMEOUT");
      return;
    }
  }
}

async function _getCalculationState(quadToken, rootState, computationId) {
  const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
  const response = await voyageService.getVoyageCalculationState(
    quadToken,
    theyrToken,
    computationId
  );
  return response;
}

async function _postVoyageCalculation(commit, rootState, voyageId) {
  const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
  try {
    const computation = await voyageService.postVoyageCalculation(
      localStorage.getItem("quad_token"),
      theyrToken,
      voyageId
    );

    return computation;
  }
  catch (error) {
    if (error instanceof HttpError) {
      commit("SET_CALCULATION_FAILED", `${error.message}`);
      return;
    }
  }
}

async function _updateVesselCoordinates(rootState, voyageId) {
  const theyrToken = rootState.weatherRouting.weatherRoutingAccessToken;
  const updateVesselPositionData = new UpdateVesselPositionData(
    state.latitude,
    state.longitude,
    `${state.date}T${state.time}`
  );
  await voyageSolutionService.updateVesselPosition(
    localStorage.getItem("quad_token"),
    theyrToken,
    voyageId,
    updateVesselPositionData
  );
}

function _generateFeatureDataHTML(feature) {
  return `
        <div class="vessel-popup-content">
            <div class="popup-title">Position ${feature.position}</div>
            <div class="popup-item">
                <i class="mdi mdi-clock-outline"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Time</span>
                    <span class="popup-value">${feature.date},${feature.time}</span>
                </div>
            </div>
            <div class="popup-item">
                <i class="mdi mdi-speedometer"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Speed</span>
                    <span class="popup-value">${feature.speed.toFixed(2)} knots</span>
                </div>
            </div>
            <div class="popup-item">
                <i class="mdi mdi-compass"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Direction</span>
                    <span class="popup-value">${feature.direction.toFixed(2)} degrees</span>
                </div>
            </div>
            <div class="popup-item">
                <i class="mdi mdi-gas-station-outline"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Daily Fuel Consumption (iso)</span>
                    <span class="popup-value">${feature.dailyFuelConsumption.toFixed(3)} metric tons</span>
                </div>
            </div>
            <div class="popup-item">
                <i class="mdi mdi-map-marker-outline"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Coordinates</span>
                    <span class="popup-value">Longitude: ${feature.coordinates[0].toFixed(2)}, Latitude: ${feature.coordinates[1].toFixed(2)}</span>
                </div>
            </div>
        </div>
    `;
}

function _generateMeshFeatureDataHTML(feature) {
  return `
        <div class="vessel-popup-content">
            <div class="popup-title">Position ${feature.position} (Solution mesh)</div>
            <div class="popup-item">
                <i class="mdi mdi-map-marker-outline"></i>
                <div class="popup-item-content">
                    <span class="popup-label">Coordinates</span>
                    <span class="popup-value">Longitude: ${feature.coordinates[0].toFixed(2)}, Latitude: ${feature.coordinates[1].toFixed(2)}</span>
                </div>
            </div>
        </div>
    `;
}

function _generateTimeSeriesData(series, tag, yAxisTitle, showxAxis = true, showLegend = true, tickInterval, chartTitle) {
  return {
    tag: tag,
    credits: {
      enabled: false,
    },
    time: {
      useUTC: true,
    },
    chart: {
      zoomType: "xy",
      height: 300,

    },
    title: {
      text: chartTitle,
      align: "right",
      x: -10,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        color: "var(--color-text)",
      }
    },

    yAxis: {
      title: {
        text: yAxisTitle,
        align: "high",
      },
      tickInterval: tickInterval,
    },
    xAxis: {
      type: "datetime",
      labels: {
        enabled: showxAxis,
      },
      gridLineWidth: 1,
      crosshair: true,
    },
    exporting: {
      enabled: false,
    },
    legend: {
      layout: "horizontal",
      align: "right",
      verticalAlign: "top",
      enabled: showLegend,
    },
    tooltip: {
      shared: true,
    },
    series: series.map((series) => {
      return {
        name: series.name,
        data: series.data,
        type: "line",
        marker: {
          enabled: true,
          radius: 2.5,
          symbol: "circle",
          states: {
            hover: {
              enabled: true,
            }
          }
        },
        animation: false,
        states: {
          hover: {
            enabled: false
          },
          normal: {
            animation: false
          },
          inactive: {
            opacity: 1
          }
        },
        tooltip: {
          enabled: true,
          pointFormatter: function () {
            return `${this.series.name}: ${this.y.toFixed(2)} <br/>`;
          }
        }
      };
    })
  };
}

export const weatherRoutingVoyageDetailsModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
